import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MaskPipe } from 'ngx-mask';
import Utils from 'src/app/helpers/utils';
import { ApiService } from 'src/app/services/api/api.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ApprovalService } from 'src/app/services/approval/approval.service';

@Component({
  selector: 'app-dashboard-approval-risk',
  templateUrl: './dashboard-approval-risk.component.html',
  styleUrls: ['./dashboard-approval-risk.component.scss'],
})
export class DashboardApprovalRiskComponent implements OnInit, OnDestroy {
  @Input() comparingVersions: boolean = false;
  @Input() isFund: boolean = false;
  data: RiskResponse;
  mainData: RiskResponse;
  comparingData: RiskResponse;
  loading = true;
  mainLoading = false;
  comparingLoading = false;

  private eventVersion: any;
  private eventMainVersion: any;
  private eventComparingVersion: any;

  constructor(
    private maskPipe: MaskPipe,
    private api: ApiService,
    private route: ActivatedRoute,
    private authService: AuthService,
    public approvalService: ApprovalService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (this.comparingVersions) {
      this.loadComparingVersions();
    } else {
      if (this.approvalService.approvalData.risk) {
        this.data = this.approvalService.approvalData.risk;
        this.loading = false;
      } else {
        this.getApprovalData();
      }
    }

    // Listener para mudanças de versão no modo normal (não comparação)
    this.eventVersion = this.approvalService.changeVersion.subscribe(() => {
      if (!this.comparingVersions) {
        // Incrementar contador de loading v3 quando iniciar requisição
        this.approvalService.incrementV3Loading();

        // Ativar loading quando trocar de versão
        this.loading = true;
        this.getApprovalData();
      }
    });

    this.eventMainVersion = this.approvalService.changeMainVersion.subscribe(
      () => {
        if (this.comparingVersions) {
          this.loadMainVersion();
        }
      }
    );

    this.eventComparingVersion =
      this.approvalService.changeComparingVersion.subscribe(() => {
        if (this.comparingVersions) {
          this.loadComparingVersion();
        }
      });
  }

  ngOnDestroy() {
    if (this.eventVersion) {
      this.eventVersion.unsubscribe();
    }
    if (this.eventMainVersion) {
      this.eventMainVersion.unsubscribe();
    }
    if (this.eventComparingVersion) {
      this.eventComparingVersion.unsubscribe();
    }
  }

  loadComparingVersions() {
    // Só carregar se houver versões selecionadas
    if (this.approvalService.approvalData.mainVersion) {
      this.loadMainVersion();
    }
    if (this.approvalService.approvalData.comparingVersion) {
      this.loadComparingVersion();
    }
  }

  loadMainVersion() {
    if (!this.approvalService.approvalData.mainVersion) {
      this.mainData = null;
      this.mainLoading = false;
      return;
    }
    this.mainLoading = true;
    this.cdr.detectChanges();
    this.getApprovalData('main');
  }

  loadComparingVersion() {
    if (!this.approvalService.approvalData.comparingVersion) {
      this.comparingData = null;
      this.comparingLoading = false;
      return;
    }
    this.comparingLoading = true;
    this.cdr.detectChanges();
    this.getApprovalData('comparing');
  }

  async getApprovalData(versionType: 'main' | 'comparing' | null = null) {
    try {
      const register_uuid = this.route.snapshot.params.id;

      if (!register_uuid) {
        this.setLoadingState(versionType, false);
        // Decrementar contador se estiver em modo normal
        if (!versionType && !this.comparingVersions) {
          this.approvalService.decrementV3Loading();
        }
        return;
      }

      // Se estiver em modo de comparação e não houver versão selecionada, não fazer requisição
      if (
        this.comparingVersions &&
        versionType &&
        ((versionType === 'main' &&
          !this.approvalService.approvalData.mainVersion) ||
          (versionType === 'comparing' &&
            !this.approvalService.approvalData.comparingVersion))
      ) {
        if (versionType === 'main') {
          this.mainData = null;
          this.mainLoading = false;
        } else if (versionType === 'comparing') {
          this.comparingData = null;
          this.comparingLoading = false;
        }
        return;
      }

      // Preparar parâmetros para API v3
      const params: any = {
        data_type: 'risk_information',
      };

      // Verificar se há versão específica selecionada
      let versionToUse = null;
      if (
        versionType === 'main' &&
        this.approvalService.approvalData.mainVersion
      ) {
        versionToUse = this.approvalService.approvalData.mainVersion;
      } else if (
        versionType === 'comparing' &&
        this.approvalService.approvalData.comparingVersion
      ) {
        versionToUse = this.approvalService.approvalData.comparingVersion;
      } else if (!versionType && this.approvalService.approvalData.version) {
        versionToUse = this.approvalService.approvalData.version;
      }

      if (versionToUse) {
        params.version = versionToUse;
      } else {
        params.last = true;
      }

      // Fazer requisição para API v3
      const response = await this.api.get<any>({
        route: `api/v3/registration/versions/register/${register_uuid}/`,
        params: params,
        token: true,
      });

      // Extrair dados do response v3
      const v3Data = response?.data;
      const riskVersions = v3Data?.versions?.risk_information?.versions;
      const riskInformation = riskVersions?.[0]?.data ?? null;

      // Mapear os dados do formato da API v3 para o formato esperado
      const riskData: RiskResponse | null = riskInformation
        ? {
            operating_limit: riskInformation.operating_limit?.toString() ?? '',
            risk: this.getRiskDisplay(riskInformation.risk) ?? '',
            web_corp_migrated_framework:
              riskInformation.web_corp_migrated_framework?.toString() ?? '',
            main_activity: riskInformation.main_activity ?? '',
            observations: riskInformation.observations ?? '',
            equity: riskInformation.equity?.toString() ?? '',
          }
        : null;

      if (versionType === 'main') {
        this.mainData = riskData;
        this.mainLoading = false;
        this.cdr.detectChanges();
      } else if (versionType === 'comparing') {
        this.comparingData = riskData;
        this.comparingLoading = false;
        this.cdr.detectChanges();
      } else {
        this.data = riskData;
        this.approvalService.approvalData.risk = riskData;
        this.loading = false;
      }
    } catch (error) {
      console.error('Erro ao buscar dados de risco:', error);
      this.setLoadingState(versionType, false);
    } finally {
      // Decrementar contador de loading v3 quando terminar requisição normal (não comparação)
      if (!versionType && !this.comparingVersions) {
        this.approvalService.decrementV3Loading();
      }
    }
  }

  /**
   * Helper para definir estado de loading
   */
  private setLoadingState(
    versionType: 'main' | 'comparing' | null,
    loading: boolean
  ) {
    if (versionType === 'main') {
      this.mainLoading = loading;
      this.cdr.detectChanges();
    } else if (versionType === 'comparing') {
      this.comparingLoading = loading;
      this.cdr.detectChanges();
    } else {
      this.loading = loading;
    }
  }

  /**
   * Converte o valor de risk para display
   */
  private getRiskDisplay(risk: string): string {
    if (!risk) return '';

    const riskMap: { [key: string]: string } = {
      low: 'Baixo',
      average: 'Médio',
      high: 'Alto',
      very_high: 'Muito Alto',
    };

    return riskMap[risk] || risk;
  }

  formatBrl(amount: number | string) {
    const formatted = Utils.formatBrl(Number(amount));
    return formatted;
  }

  // Métodos auxiliares para obter dados baseados no tipo de versão
  getDataForView(
    versionType: 'main' | 'comparing' | null = null
  ): RiskResponse {
    if (!this.comparingVersions) return this.data;
    if (versionType === 'main') return this.mainData;
    if (versionType === 'comparing') return this.comparingData;
    return this.data;
  }

  getLoadingForView(versionType: 'main' | 'comparing' | null = null): boolean {
    if (!this.comparingVersions) return this.loading;
    if (versionType === 'main') return this.mainLoading;
    if (versionType === 'comparing') return this.comparingLoading;
    return this.loading;
  }

  // Métodos de comparação para determinar status dos campos
  getFieldStatus(
    path: string,
    versionType: 'main' | 'comparing' | null = null
  ): 'added' | 'removed' | 'modified' | 'unchanged' {
    if (!this.comparingVersions || !versionType) return 'unchanged';

    const mainValue = this.getMainValueForComparison(path);
    const comparingValue = this.getComparingValueForComparison(path);

    // Normalizar valores (tratar string vazia como "não existe")
    const normalizedMain = this.normalizeValue(mainValue);
    const normalizedComparing = this.normalizeValue(comparingValue);

    // Se não existe no main mas existe no comparing = added
    if (
      (mainValue === undefined || normalizedMain === '') &&
      normalizedComparing !== ''
    ) {
      return versionType === 'comparing' ? 'added' : 'unchanged';
    }

    // Se existe no main mas não existe no comparing = removed
    if (normalizedMain !== '' && normalizedComparing === '') {
      return versionType === 'comparing' ? 'removed' : 'unchanged';
    }

    // Se ambos existem, verificar se são diferentes
    if (normalizedMain !== '' && normalizedComparing !== '') {
      if (normalizedMain !== normalizedComparing) {
        return 'modified';
      }
    }

    return 'unchanged';
  }

  getMainValueForComparison(path: string): any {
    if (!this.mainData) return undefined;
    const keys = path.split('.');
    let value: any = this.mainData;
    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = value[key];
      } else {
        return undefined;
      }
    }
    return value;
  }

  getComparingValueForComparison(path: string): any {
    if (!this.comparingData) return undefined;
    const keys = path.split('.');
    let value: any = this.comparingData;
    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = value[key];
      } else {
        return undefined;
      }
    }
    return value;
  }

  normalizeValue(val: any): string {
    if (val === null || val === undefined) return '';
    return String(val).trim();
  }

  getFieldComparisonIcon(
    path: string,
    versionType: 'main' | 'comparing' | null = null
  ): string {
    if (versionType !== 'comparing') return '';
    const status = this.getFieldStatus(path, versionType);
    switch (status) {
      case 'added':
        return '<img src="/assets/images/carbon-checkmark.svg" class="comparison-icon" style="width: 16px; height: 16px; margin-right: 6px;" alt="check" />';
      case 'removed':
        return '<img src="/assets/images/carbon-error-warning.svg" class="comparison-icon" style="width: 16px; height: 16px; margin-right: 6px;" alt="error" />';
      case 'modified':
        return '<img src="/assets/images/carbon-warning.svg" class="comparison-icon" style="width: 16px; height: 16px; margin-right: 6px;" alt="warning" />';
      default:
        return '';
    }
  }

  getFieldValueForDisplay(
    path: string,
    currentValue: any,
    versionType: 'main' | 'comparing' | null = null
  ): any {
    if (!this.comparingVersions || !versionType) return currentValue;
    if (versionType === 'comparing') {
      const status = this.getFieldStatus(path, versionType);
      // Se foi removido, mostrar o valor do main
      if (status === 'removed') {
        return this.getMainValueForComparison(path) ?? '--';
      }
    }
    return currentValue ?? '--';
  }

  getFieldStrikethroughClass(
    path: string,
    versionType: 'main' | 'comparing' | null = null
  ): string {
    if (!this.comparingVersions || !versionType) return '';
    if (versionType === 'comparing') {
      const status = this.getFieldStatus(path, versionType);
      if (status === 'removed') {
        return 'text-decoration-line-through';
      }
    }
    return '';
  }
}
